"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function CtaSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24" style={{ background: 'var(--color-background)' }}>
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        <div
          className="relative overflow-hidden rounded-3xl p-12 lg:p-16 noise-overlay"
          style={{ background: 'linear-gradient(135deg, #0d3a5c 0%, #1E5F8E 50%, #2472A4 100%)' }}
        >
          {/* Blobs */}
          <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #6AAF3D 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 left-20 w-60 h-60 rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, #9DCEF0 0%, transparent 70%)' }} />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
            {/* Text */}
            <div className="space-y-4 text-center lg:text-left max-w-2xl">
              <div className="reveal-on-scroll">
                <span className="inline-flex items-center gap-2 text-xs font-700 tracking-widest uppercase text-accent" style={{ fontWeight: 700 }}>
                  <span className="w-5 h-px bg-accent" />
                  立即聯絡我們 · Get In Touch
                </span>
              </div>
              <h2 className="reveal-on-scroll reveal-delay-1 font-display font-800 text-white leading-tight" style={{ fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 48px)' }}>
                需要醫療器材諮詢？
                <br />
                我們隨時為您服務
              </h2>
              <p className="reveal-on-scroll reveal-delay-2 text-white/70 leading-relaxed" style={{ fontSize: '16px' }}>
                無論是設備選購、技術諮詢或售後支援，歡迎透過電郵或 WhatsApp 聯絡我們的專業團隊。
              </p>
            </div>

            {/* CTA buttons */}
            <div className="reveal-on-scroll reveal-delay-3 flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <a
                href="https://wa.me/85270347770"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-display font-700 text-foreground transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
                style={{ background: '#25D366', fontWeight: 700, fontSize: '15px', color: '#fff' }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp 查詢
              </a>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-display font-700 text-white transition-all duration-300 hover:bg-white/20"
                style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)', fontWeight: 700, fontSize: '15px' }}
              >
                發送電郵查詢
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}